import { Auth0Provider } from '@auth0/auth0-react';
import React, { useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getConfig } from '../../config';
import { AuthProvider } from './AuthContext';

const LEGACY_KEYS = [
  'accessToken',
  'customerId',
  'userRole',
  'permissionGroup',
  'clientIdPractice',
  'isAuthInitialized',
];

if (typeof window !== 'undefined') {
  for (const key of LEGACY_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}

interface Auth0ProviderWithNavigateProps {
  children: ReactNode;
}

interface AppState {
  returnTo?: string;
}

export const Auth0ProviderWithNavigate: React.FC<
  Auth0ProviderWithNavigateProps
> = ({ children }: Auth0ProviderWithNavigateProps) => {
  const navigate = useNavigate();
  const config = getConfig();
  const missingConfigKeys = useMemo(() => {
    const keys: string[] = [];
    if (!config.domain) {
      keys.push('VITE_AUTH_DOMAIN');
    }
    if (!config.clientId) {
      keys.push('VITE_AUTH_CLIENT_ID');
    }
    return keys;
  }, [config.domain, config.clientId]);

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  useEffect(() => {
    if (missingConfigKeys.length === 0) {
      return;
    }

    console.error(
      `[Auth] Missing Auth0 configuration: ${missingConfigKeys.join(', ')}`
    );
  }, [missingConfigKeys]);

  if (missingConfigKeys.length > 0) {
    return (
      <div style={{ padding: '1rem', fontFamily: 'sans-serif' }}>
        <h2>Authentication configuration error</h2>
        <p>
          Missing required Auth0 configuration:
          {' '}
          {missingConfigKeys.join(', ')}
        </p>
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens
      useRefreshTokensFallback={false}
      authorizationParams={{
        redirect_uri: window.location.origin,
        ...(config.audience ? { audience: config.audience } : null),
        ...(config.organization ? { organization: config.organization } : null),
        ...(config.connection ? { connection: config.connection } : null),
      }}
    >
      <AuthProvider>{children}</AuthProvider>
    </Auth0Provider>
  );
};
